import { Box, IconButton, Link, Slide, Typography } from '@material-ui/core';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { APPLE_STORE_IMAGE_URL, GOOGLE_PLAY_IMAGE_URL } from 'assets/images/images';
import { getQueryParamObject, isEmpty, isPasswordValid, passwordAlertMessage } from 'utils/util';
import { AccountButton } from '../../../AccountButton/AccountButton';
import { AccountInput } from '../../../AccountInput/AccountInput';
import { AccountMobileHeader } from '../../../AccountMobileHeader/AccountMobileHeader';
import style from './FinishCreateAccountPane.scss';

interface Props {
  acceptInvite: Function;
  handleError: (err: any) => void;
  loadingState: [boolean, Dispatch<SetStateAction<boolean>>];
  handleExternalRouting: (url?: Nullable<string>) => void;
}

export const FinishCreateAccountPane: React.FC<Props> = ({ acceptInvite, handleError, loadingState, handleExternalRouting }) => {
  const location = useLocation();
  const token: string = getQueryParamObject(location?.search)?.token;
  const email: string = getQueryParamObject(location?.search)?.email;

  const passwordState: [string, Dispatch<SetStateAction<string>>] = useState<string>('');
  const confirmPasswordState: [string, Dispatch<SetStateAction<string>>] = useState<string>('');
  const maskPasswordState: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(true);
  const maskConfirmPasswordState: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(true);
  const [changed, setChanged] = useState<boolean>(false);

  useEffect(() => {
    loadingState[1](false);
  }, []);

  const submitFinishAccount = () => {
    const password: string = passwordState[0];
    loadingState[1](true);
    acceptInvite({ email, password }, token)
      .then(() => {
        handleExternalRouting();
        setChanged(true);
      })
      .catch((err) => {
        handleError(err);
      });
  };

  const confirmationMatches = (): boolean => !isEmpty(passwordState[0]) && !isEmpty(confirmPasswordState[0]) && passwordState[0] === confirmPasswordState[0];

  const passwordConfirmationAlert = (): Nullable<string> => (!isEmpty(confirmPasswordState[0]) && !confirmationMatches() ? 'Password does not match' : undefined);

  const goToApp = (isIOSApp: boolean) => {
    const cairnsHealthScheme = 'cairnshealth://';
    const androidAppStoreLink = 'https://play.google.com/store/apps/details?id=com.koko.prod.kokoapp';
    const appleAppStoreLink = 'https://apps.apple.com/us/app/cairns-health/id1517618563';
    const sotreLink = isIOSApp ? appleAppStoreLink : androidAppStoreLink;

    // try to open the app
    window.location.assign(cairnsHealthScheme);

    setTimeout(function () {
      // if document still has focus, then the app is not installed
      if (document.hasFocus()) {
        window.location.assign(sotreLink);
      }
    }, 1500);
  };

  const onAndroidClicked = () => {
    goToApp(false);
  };

  const onAppleClicked = () => {
    goToApp(true);
  };

  return (
    <React.Fragment>
      <Box className={style.inputBox}>
        <AccountMobileHeader />
        <Slide direction="up" in={changed} mountOnEnter unmountOnExit>
          <Box>
            <Typography className={style.mainHeader}>
              Woohoo! You did it 🤩
              <br /> <br />
            </Typography>
            <Typography className={style.instructionHeader}>
              You can access the Cairns Clinical Portal at&nbsp;
              <Link target="_blank" href="https://app.cairns.ai">
                cairns.ai
              </Link>
              <br /> <br />
            </Typography>
            <Typography className={style.instructionHeader}>
              Now, open the Cairns Health app on your phone, and let's get this party started. ⚡️
              <br /> <br />
              If you haven't already downloaded the app, please do so here:
              <br /> <br />
            </Typography>
            <IconButton className={style.storeButton} onClick={onAppleClicked}>
              <img src={APPLE_STORE_IMAGE_URL} className={style.backIcon} />
            </IconButton>
            <Typography hidden={!changed} className={style.closeInstruction}>
              (You can close this tab)
            </Typography>
          </Box>
        </Slide>
        {!changed && (
          <React.Fragment>
            <Box>
              <Typography className={style.mainHeader}>Let's Get You Set up!</Typography>
              <Typography className={style.instructionHeader}>Please create a password:</Typography>
            </Box>
            <AccountInput
              inputState={passwordState}
              maskInputState={maskPasswordState}
              placeholder="Password"
              includeValidation={true}
              validationMessage={passwordAlertMessage(passwordState[0])}
              cssClass={style.marginTop}
            />
            <AccountInput
              inputState={confirmPasswordState}
              maskInputState={maskConfirmPasswordState}
              placeholder="Confirm password"
              includeValidation={true}
              validationMessage={passwordConfirmationAlert()}
            />
            <AccountButton
              label="Easy Peasy 🤙🏻"
              onClick={submitFinishAccount}
              loading={loadingState[0]}
              disabled={!isPasswordValid(passwordState[0]) || !confirmationMatches()}
              cssClass={style.marginTop}
            />
          </React.Fragment>
        )}
      </Box>
    </React.Fragment>
  );
};
